import { FC, useMemo, useState } from 'react';

import ReactTooltip from 'react-tooltip';
import { useAppSelector } from 'hooks/redux';
import { getText } from 'utils/stringUtil';
import {
  PR_RECOMMENDATION,
  PR_INFO,
  PR_PLAN_DETAILS,
  PR_HSA,
} from 'constants/languageConstants';

import { ReactComponent as RankIcon } from 'images/plan-recommender/icon-rank.svg';
import { ReactComponent as RankIconGreen } from 'images/plan-recommender/icon-rank-green.svg';
import { ReactComponent as RankIconStar } from 'images/plan-recommender/icon-rank-star.svg';
import { ReactComponent as RankIconStarGreen } from 'images/plan-recommender/icon-rank-star-green.svg';
import {
  DEFAULT_TEXT,
  PLAN_RECOMMENDER_PLAN_NOTES,
  RATE_TIERS,
  SUPER_SCRIPTS,
} from 'modules/planRecommender/pages/constants';
import {
  currencyFormatter,
  currencyFormatterForSignedNumbers,
} from 'utils/commonUtil';
import { getCardFieldValue } from 'utils/planRecommenderUtils/planRecommenderUtils';
import { PLAN_TYPE_HSA } from 'modules/benefitsWellness/constants/BenefitConstants';
import infoIcon from 'images/icons/icon-info.png';
import playButton from 'images/icons/play-button.png';
import metadata from 'data/metadata';
import PlanDetailsPopup from 'modules/planRecommender/components/PlanDetailsPopup/PlanDetailsPopup';
import PlanTypeInfoDrawer from 'modules/benefitsWellness/components/PlanTypeInfoDrawer/PlanTypeInfoDrawer';
import {
  useMultiTableHeightSync,
  useTableHeightSync,
} from 'modules/benefitsWellness/components/TableContext/TableContextProvider';

import styles from './planRecommenderPlan.module.less';

type PlanRecommenderPlanProps = {
  plan: any;
  index: number;
  rank: number;
  activePlan: number;
  onClick: Function;
  isNTierPlan: boolean;
  dbgPlanFrequency: string;
  maxRxTiers: number;
};

const PlanRecommenderPlan: FC<PlanRecommenderPlanProps> = (
  props: PlanRecommenderPlanProps
) => {
  const {
    index,
    activePlan,
    plan,
    rank,
    isNTierPlan,
    onClick,
    dbgPlanFrequency,
    maxRxTiers,
  } = props;
  const { planRecommenderPlanKeys: keys } = metadata;
  const [isOpenPlanDetails, setOpenPlanDetails] = useState<boolean>(false);
  const [isOpenPlanInfo, setOpenPlanInfo] = useState<boolean>(false);

  const language = useAppSelector((state) => state.benguide.data?.language);

  const fieldNames = [
    keys[0].fieldName,
    keys[1].fieldName,
    keys[2].fieldName,
    keys[3].fieldName,
    keys[4].fieldName,
    keys[5].fieldName,
    keys[6].fieldName,
    keys[7].fieldName,
    'hsa',
    'annualCost',
    ...Array.from({ length: maxRxTiers }, (_, i) => keys[i + 8].fieldName),
  ];

  const { heights, refs } = useMultiTableHeightSync(fieldNames, index);

  // Additional height sync for plan name section
  const { height: planNameHeight, ref: planNameRef } = useTableHeightSync(
    'planName',
    index
  );

  const togglePlanDetails = () => {
    setOpenPlanDetails(!isOpenPlanDetails);
  };

  const togglePlanInfo = () => {
    setOpenPlanInfo(!isOpenPlanInfo);
  };
  const getRankIcon = (rank: number) => {
    if (rank === 1) {
      return (
        <div className={styles.rankWrapper}>
          {index !== activePlan ? (
            <RankIconStar className={styles.rankIcon} />
          ) : (
            <RankIconStarGreen className={styles.rankIcon} />
          )}
          <span>{getText(PR_RECOMMENDATION, language)}</span>
        </div>
      );
    } else {
      return (
        <div className={styles.rankWrapper}>
          {index !== activePlan ? (
            <RankIcon className={styles.rankIcon} />
          ) : (
            <RankIconGreen className={styles.rankIcon} />
          )}
          <div className={styles.rankText}>#{rank}</div>
        </div>
      );
    }
  };

  const filterRateTier = (plan: any) => {
    return RATE_TIERS.find((tier) => tier.fourTierValue === plan.rateTier);
  };

  const formatFieldValue = (
    key: any,
    isTooltip = false,
    hasDollar = true,
    shouldLowerCase = true
  ) => {
    const rate = filterRateTier(plan);
    const network = 'InNetwork';
    let value = getCardFieldValue(
      plan,
      key,
      network,
      rate,
      hasDollar,
      shouldLowerCase,
      dbgPlanFrequency
    );
    value =
      !value || value.toString().trim().length === 0
        ? 'defaultValue' in key
          ? key.defaultValue
          : DEFAULT_TEXT
        : value;
    return clearSuperScripts(value, !isTooltip);
  };

  const clearSuperScripts = (value: any, withElement = true) => {
    const fieldValue = SUPER_SCRIPTS.reduce(
      (current: any, superscript: any) => {
        const search = new RegExp(superscript.strValue, 'g');
        if (current.indexOf(superscript.strValue) >= 0) {
          return current.replace(search, '');
        } else {
          return current;
        }
      },
      value
    );
    if (withElement) {
      return <>{fieldValue}</>;
    } else {
      return fieldValue;
    }
  };

  const rxTierColumn = useMemo(() => {
    const getcolumns = (divCount: number) => {
      const rxColumns = [];
      for (let i = 8; i <= divCount + 7; i++) {
        const fieldName = keys[i].fieldName;
        const syncedHeight = heights[fieldName];
        rxColumns.push(
          <div
            key={`rxTier-${i}-${fieldName}`}
            ref={refs[fieldName]}
            style={{ height: syncedHeight }}
            className={
              i % 2
                ? `${styles.valueField} `
                : `${styles.valueField} ${styles.darkBg}`
            }
            title={formatFieldValue(keys[i], true)}
          >
            {formatFieldValue(keys[i])}
          </div>
        );
      }

      return <>{rxColumns}</>;
    };

    return getcolumns(maxRxTiers);

    // eslint-disable-next-line
  }, [maxRxTiers, heights, refs]);

  const {
    benefitCarrier,
    name,
    estAnnualBreakdown = {},
    type: planType,
  } = plan;

  const {
    estAnnualCost = 0,
    hsaContribution = 0,
    hraContribution = 0,
  } = estAnnualBreakdown;
  const { type, hsaCompatible } = plan;
  const rateTier = filterRateTier(plan) as any;

  const planClasses = [styles.plan];
  if (index === activePlan) {
    planClasses.push(styles.activePlan);
  }

  return (
    <div
      className={planClasses.join(' ')}
      onClick={() => {
        onClick(index);
      }}
    >
      {getRankIcon(rank)}
      <div className={`${isNTierPlan ? styles.noPremium : ''} ${styles.upper}`}>
        <div className={styles.headerBorder}></div>
        <img
          className={styles.carrierLogo}
          src={benefitCarrier?.logoUrl}
          alt=""
        />
        <div
          className={styles.planName}
          ref={planNameRef}
          style={{ height: planNameHeight }}
        >
          <div className={styles.planNameContent} title={name}>
            {name}
          </div>
          <img
            src={infoIcon}
            alt="info"
            className={styles.infoTooltipIcon}
            data-tip={
              hsaCompatible
                ? PLAN_RECOMMENDER_PLAN_NOTES[PLAN_TYPE_HSA]
                : PLAN_RECOMMENDER_PLAN_NOTES[type]
            }
          />
          <ReactTooltip
            className={styles.infoTooltip}
            place="right"
            type="light"
            effect="solid"
            border={true}
            borderColor="gray"
          />
        </div>
        <div className={styles.links}>
          {type && (
            /** FIXME: Language is not included here due to UI issues */
            <div className={styles.infoLink} onClick={togglePlanInfo}>
              <img src={playButton} alt="play" />
              {hsaCompatible ? getText(PR_HSA) : planType} {getText(PR_INFO)}
            </div>
          )}
          <div className={styles.detailsLink} onClick={togglePlanDetails}>
            {getText(PR_PLAN_DETAILS)}
          </div>
        </div>
        {!isNTierPlan && (
          <div
            ref={refs[keys[0].fieldName]}
            style={{ height: heights[keys[0].fieldName] }}
            className={`${styles.valueField} ${styles.monthlyPremium}`}
          >
            <div
              className={styles.value}
              title={formatFieldValue(keys[0], true)}
            >
              {formatFieldValue(keys[0])}
            </div>
            <div className={styles.cover}>{rateTier.coverageLabel}</div>
          </div>
        )}
        <div
          ref={refs[keys[1].fieldName]}
          style={{ height: heights[keys[1].fieldName] }}
          className={styles.valueField}
          title={formatFieldValue(keys[1], true)}
        >
          {formatFieldValue(keys[1], false, false, false)}
        </div>
        <div
          ref={refs[keys[2].fieldName]}
          style={{ height: heights[keys[2].fieldName] }}
          className={`${styles.valueField} ${styles.darkBg}`}
          title={formatFieldValue(keys[2], true)}
        >
          {formatFieldValue(keys[2])}
        </div>
        <div
          ref={refs[keys[3].fieldName]}
          style={{ height: heights[keys[3].fieldName] }}
          className={styles.valueField}
          title={formatFieldValue(keys[3], true)}
        >
          {formatFieldValue(keys[3])}
        </div>
        <div
          ref={refs[keys[4].fieldName]}
          style={{ height: heights[keys[4].fieldName] }}
          className={`${styles.valueField} ${styles.darkBg}`}
          title={formatFieldValue(keys[4], true)}
        >
          {formatFieldValue(keys[4])}
        </div>
        <div
          ref={refs[keys[5].fieldName]}
          style={{ height: heights[keys[5].fieldName] }}
          className={styles.valueField}
          title={formatFieldValue(keys[5], true)}
        >
          {formatFieldValue(keys[5])}
        </div>
        <div
          ref={refs[keys[6].fieldName]}
          style={{ height: heights[keys[6].fieldName] }}
          className={`${styles.valueField} ${styles.darkBg}`}
          title={formatFieldValue(keys[6], true)}
        >
          {formatFieldValue(keys[6])}
        </div>
        <div
          ref={refs[keys[7].fieldName]}
          style={{ height: heights[keys[7].fieldName] }}
          className={styles.valueField}
          title={formatFieldValue(keys[7], true)}
        >
          {formatFieldValue(keys[7])}
        </div>
        {rxTierColumn}

        <div
          ref={refs['hsa']}
          style={{ height: heights['hsa'] }}
          className={`${styles.valueField} ${styles.hsa}`}
          title={`${currencyFormatter(hsaContribution)} / ${currencyFormatter(
            hraContribution
          )}`}
        >
          {currencyFormatter(hsaContribution)}/
          {currencyFormatter(hraContribution)}
        </div>
        <div className={styles.fieldSeparator}></div>
        <div
          ref={refs['annualCost']}
          style={{ height: heights['annualCost'] }}
          className={`${styles.valueField} ${styles.annualCost}`}
          title={currencyFormatterForSignedNumbers(estAnnualCost)}
        >
          {currencyFormatterForSignedNumbers(estAnnualCost)}
        </div>
      </div>
      <div className={styles.planFooter}></div>
      <PlanTypeInfoDrawer
        isDrawerOpen={isOpenPlanInfo}
        onClose={() => setOpenPlanInfo(false)}
        isHSA={plan.hsaCompatible}
        planType={plan.type}
      />
      <PlanDetailsPopup
        isdrawerOpen={isOpenPlanDetails}
        onHide={togglePlanDetails}
        plan={plan}
        planId={plan.id}
        selectedRate={rateTier}
        networkType={'InNetwork'}
        planType={'medical'}
      />
    </div>
  );
};

export default PlanRecommenderPlan;
